// Chatbot Widget Constants and Configuration

export const WIDGET_CONSTANTS = {
  FILE_UPLOAD: {
    MAX_SIZE: 5, // MB
    ALLOWED_TYPES: ["image/jpeg", "image/png"],
    MAX_DIMENSIONS: { width: 50, height: 50 },
    ACCEPT_TYPES: ".jpg,.jpeg,.png",
  },
  LAUNCHER_UPLOAD: {
    MAX_SIZE: 5, // MB
    ALLOWED_TYPES: ["image/jpeg", "image/png"],
    MAX_DIMENSIONS: { width: 25, height: 25 },
    ACCEPT_TYPES: ".jpg,.jpeg,.png",
  },
  VALIDATION: {
    MAX_TEXT_LENGTH: 100,
    HEX_COLOR_REGEX: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
    REQUIRED_FIELD_MESSAGE: "fieldRequired",
    INVALID_FILE_TYPE_MESSAGE: "invalidFileType",
    FILE_TOO_LARGE_MESSAGE: "fileTooLarge",
    INVALID_DIMENSIONS_MESSAGE: "invalidDimensions",
    TEXT_TOO_LONG_MESSAGE: "textTooLong",
  },
  FIELD_TYPES: {
    FILE: "file",
    COLOR: "color",
    TEXT: "text",
    TEXTAREA: "textarea",
    TOGGLE: "toggle",
  },
  DEFAULT_VALUES: {
    BRAND_COLOR: "#1677FF",
    BUTTON_COLOR: "#1677FF",
    LAUNCHER_COLOR: "#1677FF",
    WIDGET_HEADER: "Welcome to EngagedAI",
    WELCOME_MESSAGE: "Hi there 👋\nHow can we help?",
    TEXT_US: "Text Us",
    TEXT_US_COLOR: "#FFFFFF",
    REMOVE_BRANDING: false,
    SHOW_MESSAGE_TEXTBOX: true,
  },
  BRANDING_TEXT: {
    POWERED_BY: "Power by EngagedAI",
  },
  DRAWER_MODES: {
    CREATE: "create",
    EDIT: "edit",
  },
  WIDGET_MODES: {
    CREATE: "create",
    EDIT: "edit",
  },
  S3_PATHS: {
    WIDGET_ASSETS_FOLDER: "/widgetAssets",
  },
};

// Field configurations for appearance form
export const APPEARANCE_FIELD_CONFIGS = {
  widgetIcon: {
    type: WIDGET_CONSTANTS.FIELD_TYPES.FILE,
    label: "widgetIcon",
    required: false,
    validation: {
      fileTypes: WIDGET_CONSTANTS.FILE_UPLOAD.ALLOWED_TYPES,
      maxSize: WIDGET_CONSTANTS.FILE_UPLOAD.MAX_SIZE,
      dimensions: WIDGET_CONSTANTS.FILE_UPLOAD.MAX_DIMENSIONS,
      accept: WIDGET_CONSTANTS.FILE_UPLOAD.ACCEPT_TYPES,
    },
    placeholder: "uploadWidgetIcon",
    helpText: "widgetIconHelp",
  },
  widgetHeader: {
    type: WIDGET_CONSTANTS.FIELD_TYPES.TEXT,
    label: "widgetHeader",
    required: true,
    validation: {
      maxLength: WIDGET_CONSTANTS.VALIDATION.MAX_TEXT_LENGTH,
    },
    placeholder: "enterWidgetHeader",
    defaultValue: WIDGET_CONSTANTS.DEFAULT_VALUES.WIDGET_HEADER,
  },
  welcomeMessage: {
    type: WIDGET_CONSTANTS.FIELD_TYPES.TEXTAREA,
    label: "welcomeMessage",
    required: true,
    validation: {
      maxLength: 200,
    },
    placeholder: "enterWelcomeMessage",
    defaultValue: WIDGET_CONSTANTS.DEFAULT_VALUES.WELCOME_MESSAGE,
  },
  brandColor: {
    type: WIDGET_CONSTANTS.FIELD_TYPES.COLOR,
    label: "brandColor",
    required: true,
    validation: {
      pattern: WIDGET_CONSTANTS.VALIDATION.HEX_COLOR_REGEX,
    },
    placeholder: "selectBrandColor",
    defaultValue: WIDGET_CONSTANTS.DEFAULT_VALUES.BRAND_COLOR,
  },
  buttonColor: {
    type: WIDGET_CONSTANTS.FIELD_TYPES.COLOR,
    label: "buttonColor",
    required: true,
    validation: {
      pattern: WIDGET_CONSTANTS.VALIDATION.HEX_COLOR_REGEX,
    },
    placeholder: "selectButtonColor",
    defaultValue: WIDGET_CONSTANTS.DEFAULT_VALUES.BUTTON_COLOR,
  },
  launcherIcon: {
    type: WIDGET_CONSTANTS.FIELD_TYPES.FILE,
    label: "launcherIcon",
    required: false,
    validation: {
      fileTypes: WIDGET_CONSTANTS.LAUNCHER_UPLOAD.ALLOWED_TYPES,
      maxSize: WIDGET_CONSTANTS.LAUNCHER_UPLOAD.MAX_SIZE,
      dimensions: WIDGET_CONSTANTS.LAUNCHER_UPLOAD.MAX_DIMENSIONS,
      accept: WIDGET_CONSTANTS.LAUNCHER_UPLOAD.ACCEPT_TYPES,
    },
    placeholder: "uploadLauncherIcon",
    helpText: "launcherIconHelp",
  },
  launcherText: {
    type: WIDGET_CONSTANTS.FIELD_TYPES.TEXT,
    label: "launcherText",
    required: true,
    validation: {
      maxLength: 50,
    },
    placeholder: "enterLauncherText",
    defaultValue: WIDGET_CONSTANTS.DEFAULT_VALUES.TEXT_US,
  },
  textUsColor: {
    type: WIDGET_CONSTANTS.FIELD_TYPES.COLOR,
    label: "launcherTextColor",
    required: true,
    validation: {
      pattern: WIDGET_CONSTANTS.VALIDATION.HEX_COLOR_REGEX,
    },
    placeholder: "selectLauncherTextColor",
    defaultValue: WIDGET_CONSTANTS.DEFAULT_VALUES.TEXT_US_COLOR,
  },
  launcherColor: {
    type: WIDGET_CONSTANTS.FIELD_TYPES.COLOR,
    label: "launcherColor",
    required: true,
    validation: {
      pattern: WIDGET_CONSTANTS.VALIDATION.HEX_COLOR_REGEX,
    },
    placeholder: "selectLauncherColor",
    defaultValue: WIDGET_CONSTANTS.DEFAULT_VALUES.LAUNCHER_COLOR,
  },
  removeBranding: {
    type: WIDGET_CONSTANTS.FIELD_TYPES.TOGGLE,
    label: "",
    required: false,
    placeholder: "removeBrandingDescription",
    toggleTitle: "Remove EngagedAi branding",
    toggleDescription: `A below banner: ${WIDGET_CONSTANTS.BRANDING_TEXT.POWERED_BY}`,
    defaultValue: WIDGET_CONSTANTS.DEFAULT_VALUES.REMOVE_BRANDING,
  },
  showMessageTextbox: {
    type: WIDGET_CONSTANTS.FIELD_TYPES.TOGGLE,
    label: "",
    required: false,
    placeholder: "showMessageTextboxDescription",
    toggleTitle: "Show message textbox",
    toggleDescription: "Allow visitors to type a message in the widget",
    defaultValue: WIDGET_CONSTANTS.DEFAULT_VALUES.SHOW_MESSAGE_TEXTBOX,
  },
};

// Error message constants
export const ERROR_MESSAGES = {
  FIELD_REQUIRED: WIDGET_CONSTANTS.VALIDATION.REQUIRED_FIELD_MESSAGE,
  INVALID_FILE_TYPE: WIDGET_CONSTANTS.VALIDATION.INVALID_FILE_TYPE_MESSAGE,
  FILE_TOO_LARGE: WIDGET_CONSTANTS.VALIDATION.FILE_TOO_LARGE_MESSAGE,
  INVALID_DIMENSIONS: WIDGET_CONSTANTS.VALIDATION.INVALID_DIMENSIONS_MESSAGE,
  TEXT_TOO_LONG: WIDGET_CONSTANTS.VALIDATION.TEXT_TOO_LONG_MESSAGE,
  UPLOAD_FAILED: "uploadFailed",
  INVALID_COLOR: "invalidColor",
};

// File validation utilities
export const FILE_VALIDATION = {
  validateFileType: (file, allowedTypes) => {
    if (!file) return true;
    return allowedTypes.includes(file.type);
  },

  validateFileSize: (file, maxSize) => {
    if (!file) return true;
    return file.size <= maxSize;
  },

  validateFileDimensions: (file, dimensions) => {
    return new Promise(resolve => {
      if (!file) {
        resolve(true);
        return;
      }

      const img = new Image();
      img.onload = () => {
        const isValid = img.width <= dimensions.width && img.height <= dimensions.height;
        resolve(isValid);
      };
      img.onerror = () => resolve(false);
      img.src = URL.createObjectURL(file);
    });
  },
};
